import type { AisleDefinition, FloorplanZone, FloorplanZoneDisplayDefaults, MediaLocation } from '../types';
import {
  resolveMapPointLayerColor,
  type MapPoint,
} from '../lib/retailerViewUtils';
import { zoneFillColor } from '../lib/floorplanZoneUtils';
import {
  DEFAULT_ZONE_DISPLAY,
  resolveZoneFillOpacity,
  resolveZoneOutlineShape,
  resolveZoneStrokeWidth,
} from '../lib/floorplanZoneOutline';
import { useRef, useState } from 'react';

interface FloorplanCanvasProps {
  zones: FloorplanZone[];
  aisles?: AisleDefinition[];
  imageUrl?: string | null;
  selectedZoneId?: string | null;
  selectedAisleId?: string | null;
  onZoneClick?: (zoneId: string) => void;
  onAisleClick?: (aisleId: string) => void;
  onZoneMove?: (zoneId: string, patch: Pick<FloorplanZone, 'x' | 'y'>) => void;
  onZoneResize?: (zoneId: string, patch: Pick<FloorplanZone, 'x' | 'y' | 'width' | 'height'>) => void;
  onZoneCreate?: (bounds: Pick<FloorplanZone, 'x' | 'y' | 'width' | 'height'>) => void;
  editable?: boolean;
  mediaLocations?: MediaLocation[];
  showMedia?: boolean;
  zoom?: number;
  zoneOutlineColors?: Record<string, string>;
  zoneDisplayDefaults?: FloorplanZoneDisplayDefaults;
  /** When true, zones render as thin outlines only (no labels or counts). */
  minimalOverlays?: boolean;
  /** Hint shown while editable; replaces default draw instructions when set. */
  drawHint?: string;
  /** Retailer map overlay points (campaigns, photos, videos, etc.) */
  mapOverlayPoints?: MapPoint[];
  onMapPointClick?: (point: MapPoint) => void;
}

type ZoneBounds = Pick<FloorplanZone, 'x' | 'y' | 'width' | 'height'>;

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se';

const MIN_ZONE_SIZE = 2;
const HANDLE_SIZE = 1.2;
const MAP_POINT_COLORS: Record<MapPoint['kind'], string> = {
  campaign: '#2563eb',
  photo: '#059669',
  video: '#7c3aed',
  media: '#0891b2',
  issue: '#dc2626',
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function clientToSvgPoint(svg: SVGSVGElement, clientX: number, clientY: number) {
  const point = svg.createSVGPoint();
  point.x = clientX;
  point.y = clientY;
  const transformed = point.matrixTransform(svg.getScreenCTM()?.inverse());
  return { x: transformed.x, y: transformed.y };
}

function normalizeRect(startX: number, startY: number, endX: number, endY: number): ZoneBounds {
  const x = clamp(Math.min(startX, endX), 0, 100);
  const y = clamp(Math.min(startY, endY), 0, 100);
  const width = clamp(Math.abs(endX - startX), 0, 100 - x);
  const height = clamp(Math.abs(endY - startY), 0, 100 - y);
  return { x, y, width, height };
}

function applyResizeHandle(
  zone: ZoneBounds,
  handle: ResizeHandle,
  point: { x: number; y: number },
): ZoneBounds {
  const right = zone.x + zone.width;
  const bottom = zone.y + zone.height;
  let x = zone.x;
  let y = zone.y;
  let width = zone.width;
  let height = zone.height;

  if (handle.includes('w')) {
    const nextX = clamp(point.x, 0, right - MIN_ZONE_SIZE);
    width = right - nextX;
    x = nextX;
  }
  if (handle.includes('e')) {
    width = clamp(point.x - zone.x, MIN_ZONE_SIZE, 100 - zone.x);
  }
  if (handle.includes('n')) {
    const nextY = clamp(point.y, 0, bottom - MIN_ZONE_SIZE);
    height = bottom - nextY;
    y = nextY;
  }
  if (handle.includes('s')) {
    height = clamp(point.y - zone.y, MIN_ZONE_SIZE, 100 - zone.y);
  }

  return { x, y, width, height };
}

function ZoneOutline({
  zone,
  isSelected,
  outlineColor,
  displayDefaults,
  minimalOverlays,
}: {
  zone: FloorplanZone;
  isSelected: boolean;
  outlineColor: string;
  displayDefaults: FloorplanZoneDisplayDefaults;
  minimalOverlays: boolean;
}) {
  const shape = resolveZoneOutlineShape(zone, displayDefaults);
  const strokeWidth = resolveZoneStrokeWidth(zone, isSelected, displayDefaults);
  const fillOpacity = minimalOverlays
    ? resolveZoneFillOpacity(displayDefaults)
    : (displayDefaults.fillOpacity ?? 0.55);
  const fill = minimalOverlays ? outlineColor : zoneFillColor(zone.type);
  const common = {
    fill,
    fillOpacity,
    stroke: outlineColor,
    strokeWidth,
    pointerEvents: 'none' as const,
  };

  if (shape === 'ellipse') {
    return (
      <ellipse
        cx={zone.x + zone.width / 2}
        cy={zone.y + zone.height / 2}
        rx={zone.width / 2}
        ry={zone.height / 2}
        {...common}
      />
    );
  }

  return (
    <rect
      x={zone.x}
      y={zone.y}
      width={zone.width}
      height={zone.height}
      rx={shape === 'rounded' ? '0.8' : '0'}
      {...common}
    />
  );
}

function ResizeHandles({
  zone,
  onBeginResize,
}: {
  zone: FloorplanZone;
  onBeginResize: (event: React.PointerEvent, handle: ResizeHandle) => void;
}) {
  const handles: Array<{ handle: ResizeHandle; cx: number; cy: number }> = [
    { handle: 'nw', cx: zone.x, cy: zone.y },
    { handle: 'ne', cx: zone.x + zone.width, cy: zone.y },
    { handle: 'sw', cx: zone.x, cy: zone.y + zone.height },
    { handle: 'se', cx: zone.x + zone.width, cy: zone.y + zone.height },
  ];

  return (
    <>
      {handles.map(({ handle, cx, cy }) => (
        <rect
          key={handle}
          x={cx - HANDLE_SIZE / 2}
          y={cy - HANDLE_SIZE / 2}
          width={HANDLE_SIZE}
          height={HANDLE_SIZE}
          fill="#2563eb"
          stroke="#ffffff"
          strokeWidth="0.15"
          className="cursor-nwse-resize"
          onPointerDown={(event) => onBeginResize(event, handle)}
        />
      ))}
    </>
  );
}

export function FloorplanCanvas({
  zones,
  aisles = [],
  imageUrl,
  selectedZoneId,
  selectedAisleId,
  onZoneClick,
  onAisleClick,
  onZoneMove,
  onZoneResize,
  onZoneCreate,
  editable = false,
  mediaLocations = [],
  showMedia = false,
  zoom = 1,
  zoneOutlineColors = {},
  zoneDisplayDefaults = DEFAULT_ZONE_DISPLAY,
  minimalOverlays = false,
  drawHint,
  mapOverlayPoints = [],
  onMapPointClick,
}: FloorplanCanvasProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<{
    zoneId: string;
    pointerId: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const drawRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
  } | null>(null);
  const resizeRef = useRef<{
    zoneId: string;
    handle: ResizeHandle;
    pointerId: number;
    startBounds: ZoneBounds;
  } | null>(null);
  const [draftRect, setDraftRect] = useState<ZoneBounds | null>(null);

  const canDraw = editable && Boolean(onZoneCreate);
  const canResize = editable && Boolean(onZoneResize);

  const beginZoneDrag = (event: React.PointerEvent, zone: FloorplanZone) => {
    if (!editable || !onZoneMove || !svgRef.current) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = clientToSvgPoint(svgRef.current, event.clientX, event.clientY);
    dragRef.current = {
      zoneId: zone.id,
      pointerId: event.pointerId,
      offsetX: point.x - zone.x,
      offsetY: point.y - zone.y,
    };
  };

  const beginDraw = (event: React.PointerEvent) => {
    if (!canDraw || !svgRef.current || event.target !== svgRef.current) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = clientToSvgPoint(svgRef.current, event.clientX, event.clientY);
    drawRef.current = {
      pointerId: event.pointerId,
      startX: point.x,
      startY: point.y,
    };
    setDraftRect({ x: point.x, y: point.y, width: 0, height: 0 });
  };

  const beginResize = (event: React.PointerEvent, zone: FloorplanZone, handle: ResizeHandle) => {
    if (!canResize || !svgRef.current) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeRef.current = {
      zoneId: zone.id,
      handle,
      pointerId: event.pointerId,
      startBounds: { x: zone.x, y: zone.y, width: zone.width, height: zone.height },
    };
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    if (!svgRef.current) return;

    const draw = drawRef.current;
    if (draw && draw.pointerId === event.pointerId) {
      const point = clientToSvgPoint(svgRef.current, event.clientX, event.clientY);
      setDraftRect(normalizeRect(draw.startX, draw.startY, point.x, point.y));
      return;
    }

    const resize = resizeRef.current;
    if (resize && resize.pointerId === event.pointerId && onZoneResize) {
      const point = clientToSvgPoint(svgRef.current, event.clientX, event.clientY);
      onZoneResize(resize.zoneId, applyResizeHandle(resize.startBounds, resize.handle, point));
      return;
    }

    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !onZoneMove) return;
    const point = clientToSvgPoint(svgRef.current, event.clientX, event.clientY);
    const zone = zones.find((item) => item.id === drag.zoneId);
    if (!zone) return;
    onZoneMove(drag.zoneId, {
      x: clamp(point.x - drag.offsetX, 0, 100 - zone.width),
      y: clamp(point.y - drag.offsetY, 0, 100 - zone.height),
    });
  };

  const handlePointerUp = (event: React.PointerEvent) => {
    if (drawRef.current?.pointerId === event.pointerId) {
      const rect = draftRect;
      drawRef.current = null;
      setDraftRect(null);
      if (rect && rect.width >= MIN_ZONE_SIZE && rect.height >= MIN_ZONE_SIZE) {
        onZoneCreate?.(rect);
      }
      return;
    }

    if (resizeRef.current?.pointerId === event.pointerId) {
      resizeRef.current = null;
    }

    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  };

  return (
    <div className="relative w-full min-h-[420px] border rounded-lg bg-slate-50 overflow-hidden">
      {imageUrl && (
        <img
          key={imageUrl}
          src={imageUrl}
          alt="Store floorplan"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
        />
      )}
      <svg
        ref={svgRef}
        viewBox="0 0 100 100"
        className={`relative w-full min-h-[420px] ${canDraw ? 'cursor-crosshair' : ''}`}
        style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
        onPointerDown={beginDraw}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <defs>
          {mapOverlayPoints.map((point, index) =>
            point.previewImageUrl ? (
              <clipPath
                key={`clip-${point.id}-${index}`}
                id={`map-point-clip-${index}`}
              >
                <circle cx={point.x} cy={point.y} r="1.45" />
              </clipPath>
            ) : null,
          )}
        </defs>

        {!imageUrl && (
          <rect x="2" y="2" width="96" height="96" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="0.5" rx="1" />
        )}

        {zones.map((zone) => {
          const isSelected = selectedZoneId === zone.id;
          const draggable = editable && Boolean(onZoneMove);
          const outlineColor = zoneOutlineColors[zone.id] ?? (isSelected ? '#2563eb' : '#64748b');
          const interactive = draggable || Boolean(onZoneClick) || (canResize && isSelected);
          return (
            <g
              key={zone.id}
              onClick={() => onZoneClick?.(zone.id)}
              onKeyDown={(event) => {
                if (!onZoneClick || (event.key !== 'Enter' && event.key !== ' ')) return;
                event.preventDefault();
                onZoneClick(zone.id);
              }}
              role={onZoneClick ? 'button' : undefined}
              tabIndex={onZoneClick ? 0 : undefined}
              aria-label={onZoneClick ? zone.label : undefined}
              className={draggable ? 'cursor-grab active:cursor-grabbing' : onZoneClick ? 'cursor-pointer' : undefined}
            >
              <title>{zone.label}</title>
              <ZoneOutline
                zone={zone}
                isSelected={isSelected}
                outlineColor={outlineColor}
                displayDefaults={zoneDisplayDefaults}
                minimalOverlays={minimalOverlays}
              />
              {interactive && (
                <rect
                  x={zone.x}
                  y={zone.y}
                  width={zone.width}
                  height={zone.height}
                  fill="transparent"
                  onPointerDown={(event) => beginZoneDrag(event, zone)}
                />
              )}
              {canResize && isSelected && (
                <ResizeHandles zone={zone} onBeginResize={(event, handle) => beginResize(event, zone, handle)} />
              )}
            </g>
          );
        })}

        {draftRect && (
          <rect
            x={draftRect.x}
            y={draftRect.y}
            width={draftRect.width}
            height={draftRect.height}
            fill="#2563eb"
            fillOpacity={0.15}
            stroke="#2563eb"
            strokeWidth="0.35"
            strokeDasharray="1 0.6"
            pointerEvents="none"
          />
        )}

        {aisles.map((aisle) => (
          <g
            key={aisle.id}
            onClick={() => onAisleClick?.(aisle.id)}
            className={onAisleClick ? 'cursor-pointer' : undefined}
          >
            <rect
              x={aisle.x}
              y={aisle.y}
              width={aisle.width}
              height={aisle.height}
              fill={selectedAisleId === aisle.id ? '#bfdbfe' : '#ffffff'}
              fillOpacity={imageUrl ? 0.35 : 1}
              stroke={selectedAisleId === aisle.id ? '#2563eb' : '#94a3b8'}
              strokeWidth={selectedAisleId === aisle.id ? '0.25' : '0.15'}
              rx="0.3"
            />
          </g>
        ))}

        {showMedia && mediaLocations.map((media) => (
          <circle
            key={media.id}
            cx={media.floorplanX}
            cy={media.floorplanY}
            r="0.8"
            fill="#2563eb"
            stroke="#fff"
            strokeWidth="0.15"
          />
        ))}

        {mapOverlayPoints.map((point, index) => {
          const pointColor = resolveMapPointLayerColor(
            point,
            zoneOutlineColors,
            MAP_POINT_COLORS[point.kind],
          );
          return (
            <g
              key={`${point.id}-${index}`}
              role={onMapPointClick ? 'button' : undefined}
              tabIndex={onMapPointClick ? 0 : undefined}
              aria-label={
                onMapPointClick
                  ? `${point.label}${(point.count ?? 1) > 1 ? ` (${point.count} items)` : ''}`
                  : undefined
              }
              onClick={(event) => {
                event.stopPropagation();
                onMapPointClick?.(point);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                event.stopPropagation();
                onMapPointClick?.(point);
              }}
              className={onMapPointClick ? 'cursor-pointer' : undefined}
            >
            <circle
              cx={point.x}
              cy={point.y}
              r="2.15"
              fill={pointColor}
              fillOpacity="0.15"
            />
            <circle
              cx={point.x}
              cy={point.y}
              r="1.62"
              fill="#ffffff"
              stroke={pointColor}
              strokeWidth="0.28"
              style={{
                filter:
                  'drop-shadow(0 0.35px 0.55px rgba(15, 23, 42, 0.38))',
              }}
            />
            {point.previewImageUrl ? (
              <image
                href={point.previewImageUrl}
                x={point.x - 1.45}
                y={point.y - 1.45}
                width="2.9"
                height="2.9"
                preserveAspectRatio="xMidYMid slice"
                clipPath={`url(#map-point-clip-${index})`}
                pointerEvents="none"
              />
            ) : (
              <circle
                cx={point.x}
                cy={point.y}
                r="1.15"
                fill={pointColor}
              />
            )}
            {point.kind === 'video' && (
              <path
                d={`M ${point.x - 0.35} ${point.y - 0.55} L ${point.x + 0.65} ${point.y} L ${point.x - 0.35} ${point.y + 0.55} Z`}
                fill="#ffffff"
                stroke="rgba(15,23,42,0.22)"
                strokeWidth="0.08"
                pointerEvents="none"
              />
            )}
            {(point.count ?? 1) > 1 && (
              <>
                <circle
                  cx={point.x + 1.35}
                  cy={point.y - 1.35}
                  r="0.78"
                  fill={pointColor}
                  stroke="#ffffff"
                  strokeWidth="0.18"
                  pointerEvents="none"
                />
                <text
                  x={point.x + 1.35}
                  y={point.y - 1.08}
                  fill="#ffffff"
                  fontSize="0.72"
                  fontWeight="700"
                  textAnchor="middle"
                  pointerEvents="none"
                >
                  {(point.count ?? 1) > 9 ? '9+' : point.count}
                </text>
              </>
            )}
            <title>{point.label}</title>
            </g>
          );
        })}
      </svg>
      {canDraw && drawHint && (
        <p className="absolute bottom-2 left-2 right-2 text-[11px] text-slate-600 bg-white/90 rounded px-2 py-1 pointer-events-none">
          {drawHint}
        </p>
      )}
      {canDraw && !drawHint && (
        <p className="absolute bottom-2 left-2 right-2 text-[11px] text-slate-600 bg-white/90 rounded px-2 py-1 pointer-events-none">
          Click and drag on the plan to draw a zone box. Drag existing boxes to move; use corner handles to resize.
        </p>
      )}
    </div>
  );
}
